// Athena check-in outcomes on the dashboard card (owner 2026-10-14: no cron retries; a refused
// check-in is final and staff must see it).
//
// The v2 gateway makes ONE Athena check-in attempt per visit and records every outcome on its
// ledger (brainymed/api gateway/api/checkin/athena_checkin.py). GET /v1/{p}/checkin/athena/status
// lists the day's outcomes by appointment id - local DB only, it never calls Athena - with
// `attention: true` for the ones staff must act on: refused (e.g. "Check-in requirements not met
// for Insurance."), error (auth / rate limit / Athena down), cancelled (cancelled in Athena) and
// blocked (the appointment id is not provably this patient's Athena appointment). The call is
// pinned to v2 (`_gw: "v2-pinned"`, like the Billing module, fax and forms): the route exists on
// v2 only, so it skips the v1/v2 routing rules and never falls back to v1.
//
// A card flags the first such outcome among its appointments (the card row and its allappts:
// the stamped visit is not always the row the card shows), skipping visits that are no longer
// held (Cancelled / Rescheduled / No Show: nothing is left to check in - v2 leaves those out of
// `attention` too, and a cancelled sibling revived by orders/confirm used to put "Cancelled in
// Athena" on patients being seen, review 10-15). The card row's own signintime.AthenaCheckin stamp
// (same {status, reason, at, source}) is the fallback only when the list has no row for it (list
// not loaded, other days): the list is newer than the card's copy of the stamp.
// Today's cards only; one request per practice and day at most every OUTCOMES_TTL_MS, shared by
// every card on the page.
//
// Refused / failed check-ins on today's cards are a button for staff with the Visit Confirmation
// privilege: ONE check-in attempt per click (POST /v1/{p}/checkin/athena/{appointmentid}
// {manual: true}, v2-pinned; 1-2 Athena calls), then the list is reloaded. A success - also when
// the patient was already checked in on athenaNet - is recorded by v2 and clears the chip.
// Nothing ever retries automatically (owner 10-14).
//
// Response shapes (review 10-15): api.js's response interceptor already returns the BODY, not
// axios's response. The list body is {status, date, data: [rows]}, so `res.data` is the rows - a
// `res.data.data` read always came back empty in production and no list-based chip ever showed.
// The check-in POST body is flat: {status, message, appointmentid, result, reason, ...}.
// __tests__/athenaCheckinApi.test.js runs both through the real interceptors.
import { useEffect, useState } from "react";
import api from "../../api/api";
import { V2_BASE_URL } from "../../api/bases";
import { getPracticeId } from "../../config/practice";
import { visitNotHeld } from "../../utils/visitGuard";
import { clinicTodayKey, parseJourney, startDateKey } from "./visitflow";

export const ATTENTION_STATUSES = ["refused", "error", "cancelled", "blocked"];
// Outcomes a staff click can fix once the cause is fixed (insurance, Athena down). A cancelled
// appointment or a blocked id (not provably this patient's) is fixed in athenaNet, not by a click.
export const RETRY_STATUSES = ["refused", "error"];
export const OUTCOMES_TTL_MS = 60 * 1000;
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };

export const athenaCheckinStatusUrl = (practiceid, day) =>
  `/v1/${practiceid}/checkin/athena/status?date=${encodeURIComponent(day)}`;

export const athenaCheckinUrl = (practiceid, appointmentid) =>
  `/v1/${practiceid}/checkin/athena/${encodeURIComponent(appointmentid)}`;

const LABELS = {
  refused: "Athena check-in refused",
  error: "Athena check-in failed",
  cancelled: "Cancelled in Athena",
  blocked: "Athena check-in blocked",
};

const idKey = (id) => (id === null || id === undefined ? "" : String(id));

// The card row and its allappts that are still held (not Cancelled / Rescheduled / No Show).
const cardAppointmentIds = (row) => {
  const ids = visitNotHeld(row) ? [] : [row?.appointmentid];
  (Array.isArray(row?.allappts) ? row.allappts : []).forEach((a) => {
    if (!visitNotHeld(a)) ids.push(a?.appointmentid);
  });
  return [...new Set(ids.map(idKey).filter(Boolean))];
};

// The outcome a card must show, or null. outcomes: { [appointmentid]: {status, reason, attention} }.
export const athenaCheckinIssue = (row, outcomes) => {
  if (!row) return null;
  const byAppt = outcomes || {};
  for (const id of cardAppointmentIds(row)) {
    const o = byAppt[id];
    if (o && (o.attention === true || (o.attention === undefined && ATTENTION_STATUSES.includes(o.status)))) {
      return { status: o.status, reason: o.reason || "", appointmentid: id };
    }
  }
  // The stamp only stands in for a missing list row: once the list has the card row (e.g. a
  // click turned a refusal into checked_in), the card's older copy of the stamp is ignored.
  if (visitNotHeld(row) || Object.prototype.hasOwnProperty.call(byAppt, idKey(row.appointmentid))) return null;
  const stamp = parseJourney(row.signintime).AthenaCheckin;
  if (stamp && typeof stamp === "object" && ATTENTION_STATUSES.includes(stamp.status)) {
    const day = startDateKey(row.startdate);
    const at = String(stamp.at || "").slice(0, 10);
    if (!day || !at || at === day) {
      return { status: stamp.status, reason: stamp.reason || "", appointmentid: String(row.appointmentid || "") };
    }
  }
  return null;
};

// Chip label + tooltip. canRetry: the chip is the one-attempt check-in button (see the top).
// (A visit status change is no route back: care's check-in runs only while the visit has no
// queue token, and the token was minted when the check-in event fired.)
export const athenaCheckinText = (issue, { canRetry = false } = {}) => {
  if (!issue) return null;
  const label = LABELS[issue.status] || "Athena check-in problem";
  const why = issue.reason ? `${String(issue.reason).replace(/[.\s]+$/, "")}. ` : "";
  let next;
  if (issue.status === "cancelled") {
    next = "If the patient is being seen on this visit, reinstate or rebook it in athenaNet.";
  } else if (issue.status === "blocked") {
    next = "Not checked in on Athena: check this patient's appointment in athenaNet.";
  } else if (canRetry) {
    next = "Nothing retries it automatically - fix the cause, then click to check the patient in on Athena (one attempt per click).";
  } else {
    next = "Nothing retries it automatically - fix the cause, then check the patient in on athenaNet.";
  }
  return { label, title: `${label}: ${why}${next}` };
};

// ---- the day's outcomes, shared by all cards --------------------------------------------------

let cache = { key: "", at: 0, byAppt: {} };
let inflight = null;
let inflightKey = "";
let loadSeq = 0; // only the newest load writes the cache (a forced reload supersedes older ones)
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

export const resetAthenaCheckinOutcomes = () => {
  cache = { key: "", at: 0, byAppt: {} };
  inflight = null;
  inflightKey = "";
  loadSeq += 1;
  notify();
};

export const athenaCheckinOutcomes = (practiceid, day) =>
  cache.key === `${practiceid}|${day}` ? cache.byAppt : {};

// Loads the day's outcomes unless they were loaded (or failed to load) for this practice and
// day within OUTCOMES_TTL_MS; concurrent callers share one request. force (after a check-in
// click): a new request now, whatever the cache or a request already in flight holds. Never rejects.
export const loadAthenaCheckinOutcomes = (practiceid, day, now = Date.now(), { force = false } = {}) => {
  const key = `${practiceid}|${day}`;
  if (!practiceid || !day) return Promise.resolve(athenaCheckinOutcomes(practiceid, day));
  if (!force) {
    if (inflight && inflightKey === key) return inflight;
    if (cache.key === key && now - cache.at < OUTCOMES_TTL_MS) return Promise.resolve(cache.byAppt);
  }
  loadSeq += 1;
  const mine = loadSeq;
  inflightKey = key;
  inflight = Promise.resolve()
    .then(() => api.get(athenaCheckinStatusUrl(practiceid, day), V2)) // a sync throw is a failed load too
    .then((res) => {
      const byAppt = {};
      // The interceptor-unwrapped body ({status, date, data: rows}); a raw axios response is
      // tolerated too.
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : [];
      rows.forEach((r) => {
        if (r && r.appointmentid !== undefined && r.appointmentid !== null) byAppt[String(r.appointmentid)] = r;
      });
      return byAppt;
    })
    .catch(() => (cache.key === key ? cache.byAppt : {})) // v2 down / route not deployed: keep what we had
    .then((byAppt) => {
      if (mine === loadSeq) {
        cache = { key, at: now, byAppt };
        inflight = null;
        inflightKey = "";
        notify();
      }
      return byAppt;
    });
  return inflight;
};

const errorText = (e) => {
  const b = e?.response?.data || {};
  const m = b.message || (typeof b.detail === "string" ? b.detail : "");
  return m || "Could not reach the server";
};

// ONE Athena check-in attempt for a visit (a staff click), then today's list is reloaded so the
// chip follows the recorded outcome. Resolves {result, message}; never rejects.
export const checkInOnAthena = async (practiceid, appointmentid, day) => {
  let out;
  try {
    const res = await api.post(athenaCheckinUrl(practiceid, appointmentid), { manual: true, source: "care_card" }, V2);
    // The interceptor-unwrapped body is the outcome itself ({result, message, ...}); a raw axios
    // response is tolerated too.
    const body = res && typeof res === "object" && (res.result !== undefined || res.message !== undefined) ? res : res?.data;
    const b = body && typeof body === "object" ? body : {};
    out = { result: String(b.result || ""), message: String(b.message || "") };
  } catch (e) {
    out = { result: String(e?.response?.data?.result || "failed"), message: errorText(e) };
  }
  await loadAthenaCheckinOutcomes(practiceid, day, Date.now(), { force: true });
  return out;
};

// The day's outcomes for the card's visit ({} unless it is today's card), re-rendering whenever
// they load. Today's cards load them (throttled, shared with every other card) unless load is
// false (a caller that only reads what is already loaded).
export const useAthenaCheckinOutcomes = (row, { load = true } = {}) => {
  const [, setVersion] = useState(0);
  const day = startDateKey(row?.startdate);
  const isToday = Boolean(day) && day === clinicTodayKey();
  const practiceid = getPracticeId();
  useEffect(() => {
    const fn = () => setVersion((v) => v + 1);
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);
  useEffect(() => {
    if (isToday && load) loadAthenaCheckinOutcomes(practiceid, day);
  }, [row, isToday, practiceid, day, load]);
  return isToday ? athenaCheckinOutcomes(practiceid, day) : {};
};

// The card's Athena check-in issue (or null). Today's cards read the day's outcomes; other days
// use the card row's own stamp only.
export const useAthenaCheckinIssue = (row) => athenaCheckinIssue(row, useAthenaCheckinOutcomes(row));
